import {
  choiceDraftFromLegacy,
  isMultiSelectSurveyQuestion,
  normalizeConflictingSelection,
  routingDestinationsFor,
} from './choice-answer'
import { isChoiceTextTargetSelected } from './choice-text-response'
import type { SurveyAnswerPayload } from './survey-answer'
import type { SurveyDraftAnswer, SurveyQuestion } from './survey-question.types'

const STRUCTURED_STRING_TYPES = new Set<SurveyQuestion['type']>([
  'yes_no',
  'picture_choice',
])

const CHOICE_TYPES = new Set<SurveyQuestion['type']>([
  'multiple_choice',
  'checkbox',
  'dropdown',
])

function payloadWithStructuredValue(
  questionId: string,
  value: string | number | string[],
  rawText?: string,
): SurveyAnswerPayload {
  const base: SurveyAnswerPayload = { questionId, structuredValue: value }
  if (rawText?.trim()) base.rawText = rawText.trim()
  return base
}

function payloadForChoice(question: SurveyQuestion, draft: SurveyDraftAnswer): SurveyAnswerPayload {
  const { selectedIds: rawIds, otherText } = choiceDraftFromLegacy(question, draft)
  const selectedIds = isMultiSelectSurveyQuestion(question)
    ? normalizeConflictingSelection(rawIds, routingDestinationsFor(question))
    : rawIds
  const hasTextTarget = isChoiceTextTargetSelected(question, selectedIds)
  const otherRaw = hasTextTarget && otherText.trim() ? otherText.trim() : undefined

  if (isMultiSelectSurveyQuestion(question)) {
    return payloadWithStructuredValue(question.id, selectedIds, otherRaw)
  }

  const single = selectedIds[0] ?? ''
  return payloadWithStructuredValue(question.id, single, otherRaw)
}

function payloadForArrayAnswer(
  question: SurveyQuestion,
  draft: SurveyDraftAnswer,
): SurveyAnswerPayload {
  const ids = Array.isArray(draft) ? draft : []
  return payloadWithStructuredValue(question.id, ids)
}

function payloadForStructuredString(
  question: SurveyQuestion,
  draft: SurveyDraftAnswer,
): SurveyAnswerPayload {
  const value = typeof draft === 'string' ? draft : ''
  return payloadWithStructuredValue(question.id, value)
}

function payloadForOpenText(
  question: SurveyQuestion,
  draft: SurveyDraftAnswer,
): SurveyAnswerPayload {
  const text = typeof draft === 'string' ? draft : ''
  return { questionId: question.id, rawText: text }
}

function payloadForDate(
  question: SurveyQuestion,
  draft: SurveyDraftAnswer,
): SurveyAnswerPayload {
  const iso = typeof draft === 'string' ? draft : ''
  return payloadWithStructuredValue(question.id, iso)
}

function payloadForNumeric(
  question: SurveyQuestion,
  draft: SurveyDraftAnswer,
): SurveyAnswerPayload | null {
  if (typeof draft !== 'number') return null
  return payloadWithStructuredValue(question.id, draft)
}

export function buildSavePayload(
  question: SurveyQuestion,
  draft: SurveyDraftAnswer,
): SurveyAnswerPayload {
  const base = { questionId: question.id }

  if (CHOICE_TYPES.has(question.type)) {
    return payloadForChoice(question, draft)
  }
  if (question.type === 'ranking') {
    return payloadForArrayAnswer(question, draft)
  }
  if (STRUCTURED_STRING_TYPES.has(question.type)) {
    return payloadForStructuredString(question, draft)
  }
  if (
    question.type === 'short_text' ||
    question.type === 'long_text' ||
    question.type === 'video'
  ) {
    return payloadForOpenText(question, draft)
  }
  if (question.type === 'date') {
    return payloadForDate(question, draft)
  }

  const numericPayload = payloadForNumeric(question, draft)
  if (numericPayload) return numericPayload

  if (typeof draft === 'string') {
    return { ...base, rawText: draft }
  }
  return base
}
